import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import {
  CreatePropertySalesInfoForm,
  PropertySalesInfoModel,
} from '../models/propertyModel';
import {
  createPropertySalesInfoEndpoint,
  getPropertySalesInfoEndpoint,
  updatePropertySalesInfoEndpoint,
  uploadPropertySalesFileEndpoint,
  uploadPropertySalesPurchaseAndSaleDocEndpoint,
} from '../services/propertySalesService';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { updatePropertyEndpoint } from '../services/propertyService';
import { useToast } from '../../../components/toast/ToastProvider';

interface Props {
  propertyId: string;
  setErrorAlert: Function;
  baseSalePrice: number;
  // Fecha de disponibilidad actual de la propiedad (YYYY-MM-DD), ya cargada por la pantalla.
  initialAvailableAt?: string | null;
}

enum SaleFilesType {
  EXTRACTED_LETTER = 'extracted_letter',
  SCRIPTURES = 'scriptures',
}

const usePropertySalesInfo = ({
  propertyId,
  setErrorAlert,
  baseSalePrice,
  initialAvailableAt,
}: Props) => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const toast = useToast();

  const [downPayment, setDownPayment] = useState(0);
  // La fecha vive en la propiedad (no en la info de venta): se guarda con el endpoint de propiedad.
  const [availableAt, setAvailableAt] = useState('');
  useEffect(() => {
    setAvailableAt(initialAvailableAt ?? '');
  }, [initialAvailableAt]);

  const [propertySalesInfo, setPropertySalesInfo] =
    useState<PropertySalesInfoModel>();
  const {
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    register,
    reset,
    watch,
    trigger,
  } = useForm<CreatePropertySalesInfoForm>({
    resolver: yupResolver(
      yup
        .object({
          mortgage: yup.boolean(),
          free_of_lien: yup.boolean(),
          has_extracted_letter: yup.boolean(),
          has_scriptures: yup.boolean(),
          return_of_investment: yup
            .number()
            .positive('Retorno de inversión debe ser positivo')
            .min(0)
            .max(100)
            .required('Retorno de inversión es requerido'),
          internal_rate_return: yup
            .number()
            .positive('Tasa interna de retorno debe ser positivo')
            .min(0)
            .max(100)
            .required('Tasa interna de retorno es requerido'),
          earnings_percentage: yup
            .number()
            .positive('Porcentaje de ganancia debe ser positivo')
            .min(0)
            .max(100)
            .required('Porcentaje de ganancia es requerido'),
          reserve_price: yup
            .number()
            .positive('El precio de reserva debe ser positivo')
            .min(0)
            .required('El precio de reserva es requerido'),
          down_payment_price: yup
            .number()
            .positive('El precio de primar debe ser positivo')
            .min(0)
            .required('El precio de primar es requerido'),
        })
        .required(),
    ),
  });

  const loadPropertySalesInfo = async (
    propertyId: string,
  ): Promise<PropertySalesInfoModel | null> => {
    const { status, data } = await callEndpoint(
      getPropertySalesInfoEndpoint(propertyId, token),
    );
    if (status === 200) {
      const response = fetchResponseAdapter(data);
      setPropertySalesInfo(response.data);
      return response.data;
    } else {
      return null;
    }
  };

  const syncFormWithSalesInfo = (response: PropertySalesInfoModel) => {
    let downPaymentPrice = response.down_payment_price;

    if (!downPaymentPrice) {
      downPaymentPrice = baseSalePrice * 0.1;
    }

    setDownPayment(downPaymentPrice);

    reset({
      mortgage: response.mortgage || false,
      free_of_lien: response.free_of_lien || false,
      has_extracted_letter: response.has_extracted_letter || false,
      has_scriptures: response.has_scriptures || false,
      return_of_investment: response.return_of_investment,
      internal_rate_return: response.internal_rate_return,
      earnings_percentage: response.earnings_percentage,
      reserve_price: response.reserve_price,
      down_payment_price: downPaymentPrice,
    });
  };

  // Guarda solo la fecha de disponibilidad (campo de la propiedad). Devuelve false si falta o falla.
  const saveAvailability = async (): Promise<boolean> => {
    if (!availableAt) {
      toast.error('La fecha de disponibilidad es requerida.');
      return false;
    }
    const { status } = await callEndpoint(
      updatePropertyEndpoint({ available_at: availableAt }, propertyId, token),
    );
    if (status !== 200) {
      toast.error('No se pudo guardar la fecha de disponibilidad.');
      return false;
    }
    return true;
  };

  // Devuelve true solo si se guardó todo (fecha + info de venta): quien encadena otros guardados
  // se detiene si falla.
  const handlePropertySalesAction = async (
    form: CreatePropertySalesInfoForm,
  ): Promise<boolean> => {
    if (!(await saveAvailability())) return false;
    const isUpdate = !!propertySalesInfo?.id;
    if (isUpdate) {
      return updateSalesInfo(form);
    } else {
      return createSalesInfo(form);
    }
  };

  // Valida y guarda el formulario completo (valores financieros + toggles) sin pasar por un <form>:
  // lo usa la tarjeta "Configurar venta". Devuelve false si la validación o el guardado fallan.
  const submitSalesInfo = async (): Promise<boolean> => {
    let saved = false;
    await handleSubmit(async (form) => {
      saved = await handlePropertySalesAction(form);
    })();
    return saved;
  };

  const updateSalesInfo = async (
    form: CreatePropertySalesInfoForm,
  ): Promise<boolean> => {
    const { status, data } = await callEndpoint(
      updatePropertySalesInfoEndpoint(
        form,
        propertyId,
        propertySalesInfo?.id || '',
        token,
      ),
    );
    const response = fetchResponseAdapter(data);
    // update validation
    if (status !== 200) {
      setErrorAlert(
        `Error, no fue posible actualizar información, código error: ${response.code}`,
      );
      return false;
    }
    // Sin recargar la página: se vuelve a leer la información (y el detalle financiero) y se avisa con un toast.
    await loadPropertySalesInfo(propertyId);
    toast.success('Información de venta guardada.');
    return true;
  };

  const createSalesInfo = async (
    form: CreatePropertySalesInfoForm,
  ): Promise<boolean> => {
    const { status, data } = await callEndpoint(
      createPropertySalesInfoEndpoint(form, propertyId, token),
    );
    const response = fetchResponseAdapter(data);
    // update validation
    if (status !== 201) {
      setErrorAlert(
        `Error, no fue posible crear registro, código error: ${response.code}`,
      );
      return false;
    }
    await loadPropertySalesInfo(propertyId);
    toast.success('Información de venta creada.');
    return true;
  };

  const handleUploadExtractedLetter = async (files: Array<any>) => {
    if (!files.length) {
      setErrorAlert('Error, debes de adjuntar un archivo para subir');
      return;
    }
    const formData = new FormData();
    formData.append('document', files[0]);
    formData.append('type', SaleFilesType.EXTRACTED_LETTER);
    const { status } = await callEndpoint(
      uploadPropertySalesFileEndpoint(
        formData,
        propertyId,
        propertySalesInfo?.id || '',
        token,
      ),
    );
    if (status === 200) {
      await loadPropertySalesInfo(propertyId);
      toast.success('Archivo subido.');
    } else {
      setErrorAlert(
        'Lo sentimos, por el momento no es posible completar la acción',
      );
    }
  };

  const handleUploadScriptures = async (files: Array<any>) => {
    if (!files.length) {
      setErrorAlert('Error, debes de adjuntar un archivo para subir');
      return;
    }
    const formData = new FormData();
    formData.append('document', files[0]);
    formData.append('type', SaleFilesType.SCRIPTURES);
    const { status } = await callEndpoint(
      uploadPropertySalesFileEndpoint(
        formData,
        propertyId,
        propertySalesInfo?.id || '',
        token,
      ),
    );
    if (status === 200) {
      await loadPropertySalesInfo(propertyId);
      toast.success('Archivo subido.');
    } else {
      setErrorAlert(
        'Lo sentimos, por el momento no es posible completar la acción',
      );
    }
  };

  const handleUploadPurchaseAndSaleDoc = async (files: Array<any>) => {
    if (!files.length) {
      setErrorAlert('Error, debes de adjuntar un archivo para subir');
      return;
    }
    const formData = new FormData();
    formData.append('purchase_and_sale_doc', files[0]);
    const { status } = await callEndpoint(
      uploadPropertySalesPurchaseAndSaleDocEndpoint(
        formData,
        propertyId,
        propertySalesInfo?.id || '',
        token,
      ),
    );
    if (status === 200) {
      await loadPropertySalesInfo(propertyId);
      toast.success('Archivo subido.');
    } else {
      setErrorAlert(
        'Lo sentimos, por el momento no es posible completar la acción',
      );
    }
  };

  useEffect(() => {
    loadPropertySalesInfo(propertyId)
      .then((response) => {
        if (response) {
          syncFormWithSalesInfo(response);
        }
      })
      .catch((e) => console.error(e));
  }, [propertyId]);

  useEffect(() => {
    if (!propertySalesInfo) return;
    if (propertySalesInfo.down_payment_price) return;

    const nextDownPayment = baseSalePrice * 0.1;
    setDownPayment(nextDownPayment);

    const currentReservePrice = Number(getValues('reserve_price')) || 0;
    setValue('down_payment_price', nextDownPayment - currentReservePrice);
  }, [baseSalePrice, propertySalesInfo, getValues, setValue]);

  const reservePriceWatched = watch('reserve_price');

  useEffect(() => {
    setValue('down_payment_price', downPayment - reservePriceWatched);
  }, [reservePriceWatched]);

  return {
    control,
    register,
    errors,
    handleSubmit,
    handlePropertySalesAction,
    submitSalesInfo,
    saveAvailability,
    trigger,
    handleUploadExtractedLetter,
    handleUploadScriptures,
    handleUploadPurchaseAndSaleDoc,
    availableAt,
    setAvailableAt,
    propertySalesInfo,
    isLoading,
  };
};

export default usePropertySalesInfo;
